import { useInfiniteQuery } from '@tanstack/react-query'
import { QueryKeys } from '../queries/querykeys'
import { BankAPI } from '../api/bancos.api'
import { UserAuth } from '../context/AuthContext'
import { SumaryMovement } from '../models/Bank'

interface BankTransactionFilters {
  searchQuery?: string
  transactionType?: string
  account?: string
  from?: string
  to?: string
}

interface UseInfiniteBankTransactionsParams {
  businessId: number
  productId: number
  year: number
  month?: number
  limit: number
  filters: BankTransactionFilters
  enabled?: boolean
}

interface BankTransactionsPage {
  data: SumaryMovement[]
  totalPages: number
  currentPage: number
  hasNextPage: boolean
  count: number
}

export const useInfiniteBankTransactions = ({
  businessId,
  productId,
  year,
  month,
  limit = 20,
  filters,
  enabled = true
}: UseInfiniteBankTransactionsParams) => {
  const { isAuthenticated } = UserAuth()
  const bankAPI = new BankAPI()

  return useInfiniteQuery<BankTransactionsPage>({
    enabled: enabled && isAuthenticated && !!businessId,
    queryKey: [
      QueryKeys.BANK_TRANSACTIONS,
      businessId,
      productId,
      year,
      month,
      limit,
      filters
    ],
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    placeholderData: previousData => previousData,
    queryFn: async ({ pageParam }) => {
      const currentPage = Number(pageParam ?? 0)
      const response = await bankAPI.getTransactionsSummary(
        productId,
        businessId,
        limit,
        year,
        month,
        currentPage,
        filters
      ) as { libros: SumaryMovement[], count: number }

      const transactions = response.libros || []
      const count = response.count || 0

      if (!transactions || transactions.length === 0) {
        return {
          data: [],
          totalPages: 0,
          currentPage,
          hasNextPage: false,
          count: 0
        }
      }

      const totalPages = Math.ceil(count / limit)

      return {
        data: transactions,
        totalPages,
        currentPage,
        hasNextPage: currentPage + 1 < totalPages,
        count
      }
    },
    getNextPageParam: (lastPage) => {
      return lastPage.hasNextPage ? lastPage.currentPage + 1 : undefined
    },
    initialPageParam: 0
  })
}
